<template>
  <div class="main-content">
    <!-- Toolbar -->
    <div class="row mb-2">
      <div class="col-md-12 d-flex align-items-center tb-toolbar">
        <feather-icon icon="UsersIcon" size="18" class="mr-50 text-primary" />
        <small class="text-muted">Gerencie embarques e desembarques diários</small>
        <div class="ml-auto d-flex align-items-center">
          <button
            v-if="$hasAbility('equipe.atualizar')"
            class="btn btn-back btn-sm mr-1"
            @click="loadStatus()"
            :disabled="loading"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
              :class="{ spin: loading }"
            />
            Atualizar
          </button>
          <button
            v-if="$hasAbility('equipe.visualizar')"
            class="btn btn-add btn-sm"
            @click="showHistory = !showHistory"
          >
            <feather-icon
              :icon="showHistory ? 'GridIcon' : 'ClockIcon'"
              size="14"
              class="mr-50"
            />
            {{ showHistory ? "Ver Equipes" : "Histórico" }}
          </button>
        </div>
      </div>
    </div>

    <!-- STATUS CARDS -->
    <div v-if="!showHistory">
      <!-- Filtros rápidos -->
      <div class="row mb-2">
        <div class="col-md-4">
          <b-form-group class="mb-0">
            <input
              v-model="filter"
              type="text"
              class="form-control form-control-sm"
              placeholder="Filtrar equipes..."
            />
          </b-form-group>
        </div>
        <div class="col-md-8 d-flex align-items-center tb-chips">
          <button
            type="button"
            class="tb-chip"
            :aria-pressed="String(filterStatus === null)"
            @click="filterStatus = null"
          >
            Todas
            <span class="tb-chip-count">{{ summary.total }}</span>
          </button>
          <button
            type="button"
            class="tb-chip tb-chip--boarded"
            :aria-pressed="String(filterStatus === 'boarded')"
            @click="filterStatus = 'boarded'"
          >
            <feather-icon icon="CheckCircleIcon" size="12" />
            Embarcadas
            <span class="tb-chip-count">{{ summary.boarded }}</span>
          </button>
          <button
            type="button"
            class="tb-chip"
            :aria-pressed="String(filterStatus === 'available')"
            @click="filterStatus = 'available'"
          >
            <feather-icon icon="CircleIcon" size="12" />
            Disponíveis
            <span class="tb-chip-count">{{ summary.available }}</span>
          </button>
        </div>
      </div>

      <!-- Cards -->
      <div v-if="teams.length === 0" class="text-center py-5 text-muted">
        <feather-icon icon="InboxIcon" size="40" class="mb-1" />
        <p>Nenhuma equipe encontrada.</p>
      </div>

      <div
        class="row align-items-start tb-grid"
        :class="{ 'tb-grid--loading': loading }"
      >
        <div
          v-for="team in teams"
          :key="team.id"
          class="col-md-6 col-xl-4 tb-card-col mb-2"
        >
          <div
            class="card tb-card mb-0"
            :class="team.boarding ? 'tb-card--boarded' : 'tb-card--available'"
          >
            <div class="card-header tb-card-header">
              <div class="tb-card-title">
                <strong class="tb-team-name">{{ team.name }}</strong>
                <small class="d-block text-muted" v-if="team.vehicle">
                  <feather-icon icon="TruckIcon" size="12" class="mr-25" />
                  {{ team.vehicle }}
                </small>
              </div>
              <span
                class="tb-status"
                :class="
                  team.boarding ? 'tb-status--boarded' : 'tb-status--available'
                "
              >
                <feather-icon
                  :icon="team.boarding ? 'CheckCircleIcon' : 'CircleIcon'"
                  size="11"
                  class="mr-25"
                />
                {{ team.boarding ? "EMBARCADO" : "DISPONÍVEL" }}
              </span>
            </div>

            <div v-if="team.boarding" class="card-body tb-card-body">
              <div
                class="tb-clock"
                :class="'tb-clock--' + shiftSeverity(team.boarding.started_at)"
              >
                <feather-icon icon="ClockIcon" size="16" class="mr-50" />
                <span class="tb-clock-value">{{
                  shiftHHMMSS(team.boarding.started_at)
                }}</span>
                <feather-icon
                  v-if="shiftSeverity(team.boarding.started_at) !== 'ok'"
                  icon="AlertTriangleIcon"
                  size="15"
                  class="ml-50"
                />
              </div>

              <div class="tb-meta">
                desde {{ team.boarding.started_at | formatDateTime }}
                <span v-if="team.boarding.started_by">
                  · por {{ team.boarding.started_by }}</span
                >
              </div>

              <div class="tb-crew-label">
                TRIPULAÇÃO ({{
                  team.boarding.professionals
                    ? team.boarding.professionals.length
                    : 0
                }})
              </div>

              <div
                v-if="
                  team.boarding.professionals &&
                  team.boarding.professionals.length
                "
                class="tb-crew"
              >
                <div
                  v-for="p in team.boarding.professionals"
                  :key="p.id"
                  class="tb-crew-item"
                >
                  <span class="tb-avatar">{{ initials(p.name) }}</span>
                  <span class="tb-crew-text">
                    <span class="tb-crew-name">{{ p.name }}</span>
                    <span v-if="p.professional_type" class="tb-crew-type">{{
                      p.professional_type
                    }}</span>
                  </span>
                </div>
              </div>
              <div v-else class="tb-crew-empty">
                Nenhum profissional registrado.
              </div>
            </div>

            <div class="card-footer tb-card-footer">
              <button
                v-if="!team.boarding && $hasAbility('equipe.embarcar')"
                class="btn btn-add tb-action"
                @click="openBoardModal(team), loadProfessionals()"
              >
                <feather-icon icon="LogInIcon" size="13" class="mr-50" />
                Embarcar
              </button>
              <button
                v-else-if="team.boarding && $hasAbility('equipe.embarcar')"
                class="btn btn-delete btn-sm"
                @click="confirmUnboard(team)"
              >
                <feather-icon icon="LogOutIcon" size="13" class="mr-50" />
                Desembarcar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginação -->
      <div
        class="d-flex align-items-center justify-content-between mt-1 tb-pager"
        v-if="pagination.total > 0"
      >
        <div class="d-flex align-items-center">
          <small class="text-muted mr-50">Por página</small>
          <select
            class="form-control form-control-sm tb-perpage"
            :value="pagination.per_page"
            @change="setPerPage($event.target.value)"
          >
            <option v-for="v in perPageValues" :key="v" :value="v">
              {{ v }}
            </option>
          </select>
          <small class="text-muted ml-1">
            {{ pagination.total }} equipe(s)
          </small>
        </div>
        <pagination
          :options="opcoesPaginacao"
          v-if="pagination.last_page > 1"
          :records="pagination.total"
          :per-page="pagination.per_page"
          v-model="pagination.current_page"
          @paginate="loadStatus()"
        />
      </div>
    </div>

    <!-- HISTÓRICO -->
    <div v-else>
      <div class="card tb-history-card">
        <div class="card-header tb-history-header">
          <div class="d-flex align-items-center tb-history-title">
            <feather-icon icon="ClockIcon" size="16" class="mr-50 text-primary" />
            <strong>Histórico de Embarques</strong>
            <small v-if="historyPeriodLabel" class="ml-auto text-muted">
              &mdash;&nbsp;{{ historyPeriodLabel }}
            </small>
          </div>

          <div class="tb-history-filters">
            <div class="tb-filter tb-filter--team">
              <label class="tb-field-label" for="tb-hist-team">Equipe</label>
              <select
                id="tb-hist-team"
                v-model="historyFilter.team_id"
                class="form-control form-control-sm"
                @change="onHistoryTeamChange"
              >
                <option :value="null">Todas as equipes</option>
                <option v-for="t in allTeams" :key="t.id" :value="t.id">
                  {{ t.name }}
                </option>
              </select>
            </div>
            <div class="tb-filter">
              <label class="tb-field-label" for="tb-hist-from">De</label>
              <input
                id="tb-hist-from"
                v-model="historyFilter.date_from"
                type="date"
                class="form-control form-control-sm"
                @input="historyQuickRange = null"
              />
            </div>
            <div class="tb-filter">
              <label class="tb-field-label" for="tb-hist-to">Até</label>
              <input
                id="tb-hist-to"
                v-model="historyFilter.date_to"
                type="date"
                class="form-control form-control-sm"
                @input="historyQuickRange = null"
              />
            </div>
            <div class="tb-filter tb-filter--action">
              <button
                v-if="$hasAbility('equipe.visualizar')"
                class="btn btn-add btn-sm tb-action"
                @click="loadHistory({ resetPage: true })"
              >
                <feather-icon icon="SearchIcon" size="13" class="mr-50" />
                Buscar
              </button>
            </div>
          </div>

          <div class="d-flex align-items-center mt-1 tb-quick-ranges">
            <small class="text-muted mr-50">Atalhos</small>
            <button
              v-for="r in quickRanges"
              :key="r.key"
              type="button"
              class="tb-chip tb-chip--sm"
              :aria-pressed="String(historyQuickRange === r.key)"
              @click="applyQuickRange(r.key)"
            >
              {{ r.label }}
            </button>
            <small class="text-muted ml-auto tb-window-hint">
              Janela máxima de {{ historyMaxDays }} dias
            </small>
          </div>

          <div v-if="historyError" class="tb-inline-error mt-1">
            <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
            {{ historyError }}
          </div>
        </div>

        <div class="card-body p-0">
          <!-- Ainda não buscou -->
          <div v-if="!historySearched" class="tb-history-empty">
            <feather-icon icon="CalendarIcon" size="38" class="mb-1" />
            <h5 class="mb-50">Selecione um período para consultar</h5>
            <p class="mb-0">
              O histórico pode conter muitos registros, então não é carregado
              automaticamente. Escolha as datas — ou use um atalho acima — e
              clique em <strong>Buscar</strong>.
            </p>
          </div>

          <!-- Buscou e não achou -->
          <div v-else-if="history.length === 0" class="tb-history-empty">
            <feather-icon icon="InboxIcon" size="34" class="mb-1" />
            <h5 class="mb-50">Nenhum embarque encontrado</h5>
            <p class="mb-0">
              Não há registros<template v-if="historyPeriodLabel">&nbsp;{{ historyPeriodLabel }}</template
              ><template v-if="historyFilter.team_id"> para a equipe selecionada</template>.
              Tente ampliar o período.
            </p>
          </div>

          <template v-else>
            <!-- Resumo do período -->
            <div class="tb-stats">
              <div class="tb-stat">
                <span class="tb-stat-label">Embarques no período</span>
                <span class="tb-stat-value">{{ historySummary.total }}</span>
              </div>
              <div class="tb-stat">
                <span class="tb-stat-label">Em aberto</span>
                <span
                  class="tb-stat-value"
                  :class="{ 'tb-stat-value--active': historySummary.active > 0 }"
                >
                  <feather-icon
                    v-if="historySummary.active > 0"
                    icon="ActivityIcon"
                    size="15"
                    class="mr-25"
                  />{{ historySummary.active }}
                </span>
              </div>
              <div class="tb-stat">
                <span class="tb-stat-label">Duração média</span>
                <span class="tb-stat-value">
                  {{ formatSeconds(historySummary.avg_duration_seconds) }}
                </span>
              </div>
            </div>

            <div class="tb-table-scroll">
              <table class="table table-sm tb-history-table mb-0">
                <thead>
                  <tr>
                    <th style="width: 24%">Equipe</th>
                    <th>Embarque</th>
                    <th>Desembarque</th>
                    <th>Duração</th>
                    <th class="text-center" style="width: 96px">Tripulação</th>
                    <th style="width: 48px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in history" :key="item.id">
                    <td class="tb-history-team">
                      {{ item.team ? item.team.name : "—" }}
                    </td>
                    <td class="tb-num">{{ item.started_at | formatDateTime }}</td>
                    <td>
                      <span
                        v-if="!item.ended_at"
                        class="tb-status tb-status--boarded"
                      >
                        <feather-icon icon="ActivityIcon" size="11" class="mr-25" />
                        EM ABERTO
                      </span>
                      <span v-else class="tb-num">{{
                        item.ended_at | formatDateTime
                      }}</span>
                    </td>
                    <td>
                      <span
                        class="tb-duration"
                        :class="
                          item.ended_at
                            ? 'tb-duration--closed'
                            : 'tb-duration--' + shiftSeverity(item.started_at)
                        "
                      >
                        {{
                          item.ended_at
                            ? duration(item.started_at, item.ended_at)
                            : shiftHHMMSS(item.started_at)
                        }}
                      </span>
                    </td>
                    <td class="tb-num text-center">
                      {{ item.professionals ? item.professionals.length : 0 }}
                    </td>
                    <td>
                      <button
                        v-if="$hasAbility('equipe.visualizar')"
                        class="btn btn-sm btn-flat-primary p-25"
                        @click="openDetail(item)"
                        title="Ver detalhes"
                      >
                        <feather-icon icon="EyeIcon" size="14" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="tb-history-pager">
              <div class="d-flex align-items-center">
                <small class="text-muted mr-50">Por página</small>
                <select
                  class="form-control form-control-sm tb-perpage"
                  :value="historyPagination.per_page"
                  @change="setHistoryPerPage($event.target.value)"
                >
                  <option v-for="v in perPageValues" :key="v" :value="v">
                    {{ v }}
                  </option>
                </select>
                <small class="text-muted ml-1">
                  {{ historyPagination.total }} registro(s)
                </small>
              </div>
              <pagination
          :options="opcoesPaginacao"
                v-if="historyPagination.last_page > 1"
                :records="historyPagination.total"
                :per-page="historyPagination.per_page"
                v-model="historyPagination.current_page"
                @paginate="loadHistory()"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- MODAL: DETALHES DO EMBARQUE -->
    <b-modal
      id="modal-boarding-detail"
      ref="modalBoardingDetail"
      title="Detalhes do Embarque"
      size="lg"
      ok-only
      ok-title="Fechar"
      ok-variant="secondary"
    >
      <div v-if="selectedBoarding">
        <!-- Info geral -->
        <div class="row mb-2">
          <div class="col-md-6">
            <small class="text-muted d-block">Equipe</small>
            <strong>{{
              selectedBoarding.team ? selectedBoarding.team.name : "—"
            }}</strong>
          </div>
          <div class="col-md-3">
            <small class="text-muted d-block">Status</small>
            <span v-if="!selectedBoarding.ended_at" class="badge badge-success"
              >Ativo</span
            >
            <span v-else class="badge badge-secondary">Encerrado</span>
          </div>
          <div class="col-md-3">
            <small class="text-muted d-block">Duração</small>
            <span v-if="selectedBoarding.ended_at">{{
              duration(selectedBoarding.started_at, selectedBoarding.ended_at)
            }}</span>
            <span v-else class="badge badge-warning">Em andamento</span>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-6">
            <small class="text-muted d-block">Embarque em</small>
            <span>{{ selectedBoarding.started_at | formatDateTime }}</span>
          </div>
          <div class="col-md-6">
            <small class="text-muted d-block">Desembarque em</small>
            <span v-if="selectedBoarding.ended_at">{{
              selectedBoarding.ended_at | formatDateTime
            }}</span>
            <span v-else class="text-muted">—</span>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-6">
            <small class="text-muted d-block">Registrado por</small>
            <span>{{
              selectedBoarding.started_by
                ? selectedBoarding.started_by.name
                : "—"
            }}</span>
          </div>
          <div class="col-md-6">
            <small class="text-muted d-block">Finalizado por</small>
            <span>{{
              selectedBoarding.ended_by ? selectedBoarding.ended_by.name : "—"
            }}</span>
          </div>
        </div>
        <div v-if="selectedBoarding.notes" class="mb-2">
          <small class="text-muted d-block">Observações</small>
          <span>{{ selectedBoarding.notes }}</span>
        </div>
        <hr />
        <!-- Tabela de profissionais -->
        <strong class="d-block mb-1">Profissionais Embarcados</strong>
        <div
          v-if="
            !selectedBoarding.professionals ||
            !selectedBoarding.professionals.length
          "
          class="text-muted"
        >
          Nenhum profissional registrado.
        </div>
        <table v-else class="table table-sm table-bordered mb-0">
          <thead class="thead-light">
            <tr>
              <th>#</th>
              <th>Nome</th>
              <th>Tipo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in selectedBoarding.professionals" :key="p.id">
              <td>{{ i + 1 }}</td>
              <td>{{ p.name }}</td>
              <td>{{ p.professional_type || "—" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <!-- MODAL: EMBARCAR -->
    <b-modal
      id="modal-board"
      ref="modalBoard"
      title="Embarcar Equipe"
      size="xl"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="boardTarget">
        <!-- Row 1: Equipe -->
        <div class="row mb-2">
          <div class="col-md-12">
            <b-form-group label="Equipe" class="mb-0">
              <b-form-input
                :value="
                  boardTarget.name +
                  (boardTarget.vehicle ? ' — ' + boardTarget.vehicle : '')
                "
                readonly
                size="sm"
              />
            </b-form-group>
          </div>
        </div>

        <!-- Dual-list -->
        <div class="d-flex align-items-center mb-1">
          <feather-icon icon="UsersIcon" size="15" class="mr-50 text-primary" />
          <span class="font-weight-bold">Movimente os Tripulantes</span>
          <small class="ml-auto text-muted">Clique no nome para mover</small>
        </div>
        <div class="tb-dual">
          <!-- Esquerda: Não alocados -->
          <div
            class="flex-fill rounded overflow-hidden d-flex flex-column dual-list-col"
          >
            <div class="dual-list-header dual-list-header--available">
              <feather-icon icon="UserIcon" size="13" class="mr-50" />
              Disponíveis
              <b-badge variant="secondary" class="ml-auto">{{
                leftList.length
              }}</b-badge>
            </div>
            <div class="px-2 pt-2">
              <select
                v-model="filterRoleId"
                class="form-control form-control-sm mb-1"
              >
                <option :value="null">Todos os tipos</option>
                <option v-for="t in availableTypes" :key="t.id" :value="t.id">
                  {{ t.name }}
                </option>
              </select>
              <input
                v-model="leftSearch"
                type="text"
                class="form-control form-control-sm"
                placeholder="Buscar por nome..."
              />
            </div>
            <div class="dual-list-body">
              <div
                v-for="p in leftList"
                :key="p.id"
                class="dual-list-item dual-list-item--available"
                @click="moveToRight(p)"
              >
                <feather-icon
                  icon="ChevronRightIcon"
                  size="13"
                  class="mr-50 text-secondary"
                />
                <span>{{ p.name }}</span>
                <b-badge
                  v-if="p.professional_type"
                  variant="light-secondary"
                  class="ml-auto"
                  style="font-size: 0.7rem"
                  >{{ p.professional_type }}</b-badge
                >
              </div>
              <div v-if="leftList.length === 0" class="dual-list-empty">
                <feather-icon icon="InboxIcon" size="22" class="mb-50" />
                <small>Nenhum profissional disponível</small>
              </div>
            </div>
          </div>

          <!-- Setas centrais -->
          <div
            class="d-flex flex-column align-items-center justify-content-center tb-dual-arrows"
          >
            <button
              class="btn btn-add btn-sm tb-arrow-btn"
              title="Alocar todos os profissionais do filtro atual"
              @click="moveAllToRight"
            >
              Todos
              <feather-icon icon="ChevronsRightIcon" size="14" class="ml-25" />
            </button>
            <button
              class="btn btn-delete btn-sm tb-arrow-btn"
              title="Remover todos os profissionais do filtro atual"
              @click="moveAllToLeft"
            >
              <feather-icon icon="ChevronsLeftIcon" size="14" class="mr-25" />
              Todos
            </button>
          </div>

          <!-- Direita: Alocados -->
          <div
            class="flex-fill rounded overflow-hidden d-flex flex-column dual-list-col"
          >
            <div class="dual-list-header dual-list-header--selected">
              <feather-icon icon="CheckCircleIcon" size="13" class="mr-50" />
              Alocados na Equipe
              <b-badge variant="success" class="ml-auto">{{
                rightList.length
              }}</b-badge>
            </div>
            <div class="px-2 pt-2">
              <input
                v-model="rightSearch"
                type="text"
                class="form-control form-control-sm"
                placeholder="Buscar por nome..."
              />
            </div>
            <div class="dual-list-body">
              <div
                v-for="p in rightList"
                :key="p.id"
                class="dual-list-item dual-list-item--selected"
                @click="moveToLeft(p)"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="13"
                  class="mr-50 dual-list-icon dual-list-icon--keep"
                />
                <feather-icon
                  icon="XIcon"
                  size="13"
                  class="mr-50 dual-list-icon dual-list-icon--remove"
                />
                <span>{{ p.name }}</span>
                <b-badge
                  v-if="p.professional_type"
                  variant="light-success"
                  class="ml-auto"
                  style="font-size: 0.7rem"
                  >{{ p.professional_type }}</b-badge
                >
              </div>
              <div v-if="rightList.length === 0" class="dual-list-empty">
                <feather-icon icon="InboxIcon" size="22" class="mb-50" />
                <small>Nenhum profissional alocado</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Observações -->
        <b-form-group label="Observações" class="mt-2 mb-2">
          <b-form-textarea
            v-model="boardForm.notes"
            rows="2"
            placeholder="Observações opcionais..."
          />
        </b-form-group>

        <!-- Footer -->
        <div class="d-flex align-items-center mt-1" style="gap: 8px">
          <small class="tb-selected-count">
            <feather-icon icon="UsersIcon" size="13" class="mr-25" />
            <strong>{{ boardForm.professional_ids.length }}</strong>
            &nbsp;profissional(is) selecionado(s)
          </small>
          <div class="ml-auto d-flex" style="gap: 8px">
            <button class="btn btn-back" @click="$bvModal.hide('modal-board')">
              <feather-icon icon="XIcon" size="14" class="mr-50" />
              Fechar
            </button>
            <button
              class="btn btn-add"
              :disabled="boardForm.professional_ids.length === 0"
              @click="submitBoard"
            >
              <feather-icon icon="CheckIcon" size="14" class="mr-50" />
              Confirmar Embarque
            </button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- MODAL: CONFIRMAR DESEMBARQUE -->
    <b-modal
      id="modal-unboard"
      ref="modalUnboard"
      title="Confirmar Desembarque"
      ok-title="Sim, Desembarcar"
      cancel-title="Cancelar"
      ok-variant="danger"
      @ok="submitUnboard"
      no-close-on-backdrop
    >
      <div v-if="boardTarget">
        <p>
          Deseja registrar o <strong>desembarque</strong> da equipe
          <strong>{{ boardTarget.name }}</strong
          >?
        </p>
        <div v-if="boardTarget.boarding">
          <small class="text-muted">
            Embarque iniciado em
            {{ boardTarget.boarding.started_at | formatDateTime }} ({{
              shiftHHMMSS(boardTarget.boarding.started_at)
            }})
          </small>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BFormGroup,
  BFormInput,
  BFormTextarea,
} from "bootstrap-vue";
import Pagination from "vue-pagination-2";
import opcoesPaginacao from '@/libs/paginationOptions'
import ElapsedTimeMixin from "@/mixins/ElapsedTimeMixin";
import ApiErrorMixin from "@/mixins/ApiErrorMixin";

const PER_PAGE_STORAGE_KEY = "teamboarding-per-page";
const PER_PAGE_VALUES = [12, 24, 48];

// Espelha TeamBoardingController::MAX_HISTORY_DAYS. Validar no front também
// evita uma ida ao servidor só para receber 422.
const HISTORY_MAX_DAYS = 90;

const QUICK_RANGES = [
  { key: "today", label: "Hoje" },
  { key: "7d", label: "7 dias" },
  { key: "30d", label: "30 dias" },
  { key: "month", label: "Este mês" },
];

export default {
  name: "TeamBoardingIndex",

  components: {
    BBadge,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    Pagination,
  },

  filters: {
    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },

  mixins: [ElapsedTimeMixin, ApiErrorMixin],

  data() {
    return {
      loading: false,
      teams: [],
      summary: { total: 0, boarded: 0, available: 0 },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 12,
        total: 0,
      },
      perPageValues: PER_PAGE_VALUES,
      searchDebounce: null,
      // Diferença entre o relógio do servidor e o do navegador. Sem isso, uma
      // estação com a hora atrasada mostra o turno começando já com minutos.
      serverOffset: 0,
      statusRequestSeq: 0,
      historyRequestSeq: 0,
      allProfessionals: [],
      professionalTypes: [],
      history: [],
      selectedBoarding: null,
      showHistory: false,
      filter: "",
      filterStatus: null,
      filterRoleId: null,
      leftSearch: "",
      rightSearch: "",
      boardTarget: null,
      boardForm: {
        team_id: null,
        professional_ids: [],
        notes: "",
      },
      historyFilter: {
        team_id: null,
        date_from: "",
        date_to: "",
      },
      // O dropdown de equipe do histórico precisa da lista completa; `teams`
      // agora traz só a página atual.
      allTeams: [],
      historySearched: false,
      historyError: "",
      historyQuickRange: null,
      historyMaxDays: HISTORY_MAX_DAYS,
      quickRanges: QUICK_RANGES,
      historySummary: { total: 0, active: 0, avg_duration_seconds: null },
      historyPeriod: null,
      historyPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 12,
        total: 0,
      },
      refreshInterval: null,
    };
  },

  computed: {
    opcoesPaginacao() {
      return opcoesPaginacao
    },

    api() {
      return this.$store.state.api;
    },
    historyPeriodLabel() {
      if (!this.historyPeriod) return "";
      const br = (iso) => {
        const [y, m, d] = iso.split("-");
        return `${d}/${m}/${y}`;
      };
      const from = br(this.historyPeriod.from);
      const to = br(this.historyPeriod.to);
      return from === to ? `em ${from}` : `entre ${from} e ${to}`;
    },

    availableTypes() {
      // O endpoint devolve os tipos presentes no nó. Preferimos essa lista à
      // derivada dos profissionais: com um profissional por linha, um tipo cujo
      // único profissional já está embarcado desapareceria do filtro.
      if (this.professionalTypes.length) {
        return [...this.professionalTypes].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      }

      const seen = new Set();
      return this.allProfessionals
        .filter((p) => {
          if (!p.professional_type_id || seen.has(p.professional_type_id)) {
            return false;
          }
          seen.add(p.professional_type_id);
          return true;
        })
        .map((p) => ({ id: p.professional_type_id, name: p.professional_type }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    roleOptions() {
      return [
        { value: null, text: "Todos os tipos" },
        ...this.availableTypes.map((t) => ({ value: t.id, text: t.name })),
      ];
    },
    leftList() {
      const selectedIds = new Set(this.boardForm.professional_ids);
      let list = this.allProfessionals.filter((p) => !selectedIds.has(p.id));
      if (this.filterRoleId)
        list = list.filter((p) => p.professional_type_id == this.filterRoleId);
      if (this.leftSearch.trim()) {
        const q = this.leftSearch.toLowerCase();
        list = list.filter((p) => p.name.toLowerCase().includes(q));
      }
      return list;
    },
    rightList() {
      const selectedIds = new Set(this.boardForm.professional_ids);
      let list = this.allProfessionals.filter((p) => selectedIds.has(p.id));
      if (this.rightSearch.trim()) {
        const q = this.rightSearch.toLowerCase();
        list = list.filter((p) => p.name.toLowerCase().includes(q));
      }
      return list;
    },
  },

  methods: {
    // O $toastr do projeto (wrapper.vue processObjectData) descarta title e type
    // quando `msg` vem undefined, cai no defaultType (success/verde) e renderiza
    // o objeto inteiro como "[object Object]". Ou seja: passar undefined ou um
    // objeto cru transforma um erro num toast verde sem informação. Este helper
    // garante que o toast sempre receba string legível — e o erro cru vai pro
    // console, onde dá pra diagnosticar.

    async loadStatus({ resetPage = false } = {}) {
      if (resetPage) this.pagination.current_page = 1;

      const seq = this.statusRequestSeq + 1;
      this.statusRequestSeq = seq;

      this.loading = true;
      try {
        const params = new URLSearchParams({
          paginated: true,
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
        });
        if (this.filter.trim()) params.append("search", this.filter.trim());
        if (this.filterStatus) params.append("status", this.filterStatus);

        const { data } = await axios.get(
          this.api + "team-boardings/status?" + params.toString(),
        );

        if (seq !== this.statusRequestSeq) return;

        if (data.server_time) {
          this.serverOffset = new Date(data.server_time).getTime() - Date.now();
        }

        this.summary = data.summary || { total: 0, boarded: 0, available: 0 };

        const payload = data.data;
        if (payload && Array.isArray(payload.data)) {
          this.teams = payload.data;
          this.pagination.total = payload.total;
          this.pagination.per_page = payload.per_page;
          this.pagination.last_page = payload.last_page;
          this.pagination.current_page = payload.current_page;

          // Um desembarque pode esvaziar a última página (ex.: filtro
          // "Embarcadas"). Cai para a última página válida em vez de mostrar
          // tela vazia. Limitado a uma chamada extra: se last_page também
          // estiver vazia ela vale 1, e a condição deixa de ser satisfeita.
          if (payload.data.length === 0 && payload.current_page > 1) {
            this.pagination.current_page = payload.last_page;
            return this.loadStatus();
          }
        } else {
          this.teams = payload || [];
        }
      } catch (e) {
        if (seq !== this.statusRequestSeq) return;
        console.error("[TeamBoarding] loadStatus:", e);
        this.$message("Erro", this.apiErrorText(e), "error");
      } finally {
        if (seq === this.statusRequestSeq) this.loading = false;
      }
    },

    setPerPage(value) {
      const parsed = Number(value);
      this.pagination.per_page = PER_PAGE_VALUES.includes(parsed) ? parsed : 12;
      try {
        localStorage.setItem(PER_PAGE_STORAGE_KEY, this.pagination.per_page);
      } catch (e) {
        // localStorage indisponível (modo privado): segue sem persistir
      }
      this.loadStatus({ resetPage: true });
    },

    async loadProfessionals() {
      try {
        const { data } = await axios.get(
          this.api + "team-boardings/node-professionals",
        );
        this.allProfessionals = data.data || [];
        this.professionalTypes = data.types || [];
      } catch (e) {
        console.error(
          "[TeamBoarding] loadProfessionals error:",
          e?.response?.data || e?.message || e,
        );
        this.$message(
          "Erro",
          "Não foi possível carregar os profissionais disponíveis.",
          "error",
        );
      }
    },

    isoDate(d) {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },

    applyQuickRange(key) {
      const today = new Date();
      let from = new Date(today);

      if (key === "7d") from.setDate(today.getDate() - 6);
      else if (key === "30d") from.setDate(today.getDate() - 29);
      else if (key === "month")
        from = new Date(today.getFullYear(), today.getMonth(), 1);

      this.historyQuickRange = key;
      this.historyFilter.date_from = this.isoDate(from);
      this.historyFilter.date_to = this.isoDate(today);
      this.loadHistory({ resetPage: true });
    },

    formatSeconds(total) {
      if (total === null || total === undefined) return "—";
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      return h > 0 ? `${h}h ${m}min` : `${m}min`;
    },

    // Lista completa de equipes, só para o dropdown do histórico. Usa o formato
    // não-paginado do endpoint de status. Carregada sob demanda ao abrir a aba.
    async loadAllTeams() {
      try {
        const { data } = await axios.get(this.api + "team-boardings/status");
        this.allTeams = Array.isArray(data.data) ? data.data : [];
      } catch (e) {
        console.error("[TeamBoarding] loadAllTeams:", e);
      }
    },

    setHistoryPerPage(value) {
      const parsed = Number(value);
      this.historyPagination.per_page = PER_PAGE_VALUES.includes(parsed)
        ? parsed
        : 12;
      this.loadHistory({ resetPage: true });
    },

    onHistoryTeamChange() {
      if (this.historySearched) this.loadHistory({ resetPage: true });
    },

    async loadHistory({ resetPage = false } = {}) {
      const { date_from: dFrom, date_to: dTo } = this.historyFilter;

      if (!dFrom || !dTo) {
        this.historyError =
          "Informe a data inicial e a data final para consultar o histórico.";
        return;
      }

      const from = new Date(dFrom + "T00:00:00");
      const to = new Date(dTo + "T00:00:00");

      if (from > to) {
        this.historyError =
          "A data inicial não pode ser posterior à data final.";
        return;
      }

      const days = Math.round((to - from) / 86400000) + 1;
      if (days > HISTORY_MAX_DAYS) {
        this.historyError = `O período não pode exceder ${HISTORY_MAX_DAYS} dias — você selecionou ${days}.`;
        return;
      }

      this.historyError = "";
      if (resetPage) this.historyPagination.current_page = 1;

      const seq = this.historyRequestSeq + 1;
      this.historyRequestSeq = seq;

      this.$loading(true);
      try {
        const params = new URLSearchParams({
          date_from: dFrom,
          date_to: dTo,
          page: this.historyPagination.current_page,
          per_page: this.historyPagination.per_page,
        });
        if (this.historyFilter.team_id) {
          params.append("team_id", this.historyFilter.team_id);
        }

        const { data } = await axios.get(
          this.api + "team-boardings/history?" + params.toString(),
        );

        if (seq !== this.historyRequestSeq) return;

        const payload = data.data;
        this.history = payload?.data || [];
        this.historyPagination.total = payload?.total || 0;
        this.historyPagination.per_page =
          payload?.per_page || this.historyPagination.per_page;
        this.historyPagination.last_page = payload?.last_page || 1;
        this.historyPagination.current_page = payload?.current_page || 1;

        this.historySummary = data.summary || {
          total: 0,
          active: 0,
          avg_duration_seconds: null,
        };
        this.historyPeriod = data.period || null;
        this.historySearched = true;

        // Última página pode ter esvaziado entre duas consultas.
        if (
          this.history.length === 0 &&
          this.historyPagination.current_page > 1
        ) {
          this.historyPagination.current_page =
            this.historyPagination.last_page;
          return this.loadHistory();
        }
      } catch (e) {
        if (seq !== this.historyRequestSeq) return;
        console.error("[TeamBoarding] loadHistory:", e);
        this.historyError = this.apiErrorText(e);
      } finally {
        if (seq === this.historyRequestSeq) this.$loading(false);
      }
    },

    async openBoardModal(team) {
      this.boardTarget = team;
      this.boardForm = { team_id: team.id, professional_ids: [], notes: "" };
      // Pre-populate with currently boarded professionals
      if (team.boarding && team.boarding.professionals) {
        this.boardForm.professional_ids = team.boarding.professionals.map(
          (p) => p.id,
        );
      }
      this.filterRoleId = null;
      this.leftSearch = "";
      this.rightSearch = "";
      await this.loadProfessionals();
      this.$bvModal.show("modal-board");
    },

    openDetail(item) {
      this.selectedBoarding = item;
      this.$bvModal.show("modal-boarding-detail");
    },

    confirmUnboard(team) {
      this.boardTarget = team;
      this.$bvModal.show("modal-unboard");
    },

    async submitBoard() {
      if (this.boardForm.professional_ids.length === 0) {
        this.$message(
          "Atenção",
          "Selecione ao menos um profissional",
          "warning",
        );
        return;
      }
      this.$loading(true);
      try {
        await axios.post(this.api + "team-boardings/board", this.boardForm);
        this.$message("Sucesso", "Embarque registrado com sucesso!", "success");
        this.$bvModal.hide("modal-board");
        await this.loadStatus();
      } catch (e) {
        console.error("[TeamBoarding] submit:", e);
        this.$message("Erro", this.apiErrorText(e), "error");
      } finally {
        this.$loading(false);
      }
    },

    moveToRight(p) {
      if (!this.boardForm.professional_ids.includes(p.id)) {
        this.boardForm.professional_ids.push(p.id);
      }
    },

    moveToLeft(p) {
      this.boardForm.professional_ids = this.boardForm.professional_ids.filter(
        (id) => id !== p.id,
      );
    },

    moveAllToRight() {
      this.leftList.forEach((p) => {
        if (!this.boardForm.professional_ids.includes(p.id)) {
          this.boardForm.professional_ids.push(p.id);
        }
      });
    },

    moveAllToLeft() {
      const rightIds = new Set(this.rightList.map((p) => p.id));
      this.boardForm.professional_ids = this.boardForm.professional_ids.filter(
        (id) => !rightIds.has(id),
      );
    },

    async submitUnboard() {
      if (!this.boardTarget?.boarding) return;
      this.$loading(true);
      try {
        await axios.post(
          this.api +
            "team-boardings/" +
            this.boardTarget.boarding.id +
            "/unboard",
        );
        this.$message(
          "Sucesso",
          "Desembarque registrado com sucesso!",
          "success",
        );
        await this.loadStatus();
      } catch (e) {
        console.error("[TeamBoarding] submit:", e);
        this.$message("Erro", this.apiErrorText(e), "error");
      } finally {
        this.$loading(false);
      }
    },

    shiftSeconds(startedAt) {
      if (!startedAt) return 0;
      const now = this.elapsedNow + this.serverOffset;
      return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
    },

    shiftHHMMSS(startedAt) {
      if (!startedAt) return "--:--:--";
      const total = this.shiftSeconds(startedAt);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
    },

    // Limiares de turno. Não reusar elapsedBadgeClass do mixin: os 90s/180s
    // dele são de tempo-resposta de ocorrência, não de duração de turno.
    shiftSeverity(startedAt) {
      const hours = this.shiftSeconds(startedAt) / 3600;
      if (hours >= 24) return "danger";
      if (hours >= 12) return "warning";
      return "ok";
    },

    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0] ? parts[0].charAt(0) : "";
      const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
      return (first + last).toUpperCase();
    },

    duration(start, end) {
      if (!start || !end) return "—";
      const diff = Math.floor((new Date(end) - new Date(start)) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      return h > 0 ? `${h}h ${m}min` : `${m}min`;
    },
  },

  watch: {
    filter() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.loadStatus({ resetPage: true });
      }, 400);
    },

    filterStatus() {
      this.loadStatus({ resetPage: true });
    },

    showHistory(value) {
      if (value && this.allTeams.length === 0) {
        this.loadAllTeams();
      }
    },
  },

  mounted() {
    let stored = null;
    try {
      stored = Number(localStorage.getItem(PER_PAGE_STORAGE_KEY));
    } catch (e) {
      stored = null;
    }
    if (PER_PAGE_VALUES.includes(stored)) {
      this.pagination.per_page = stored;
    }

    this.loadStatus();
    this.loadProfessionals();

    // Refresh automático a cada 60 segundos, preservando página e filtros.
    // Arrow function é necessária: setInterval passa o tick como 1º argumento,
    // que cairia no destructuring de opções do loadStatus.
    this.refreshInterval = setInterval(() => this.loadStatus(), 60000);
  },

  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
};
</script>

<!--
  1) Bloco NÃO-scoped de propósito: o BootstrapVue move o conteúdo do b-modal
     para o fim do <body>. Custom properties CSS herdam pela árvore do DOM, não
     pela de componentes — tokens declarados num seletor do <style scoped> não
     alcançariam o modal de embarque. O prefixo tb- evita colisão global.

  2) Dois níveis por cor, de propósito:
       --tb-X       cor sólida, usada como FUNDO com texto branco
       --tb-X-text  cor de TEXTO sobre o fundo suave --tb-X-soft
       --tb-X-line  cor decorativa (faixa/borda), sem requisito de contraste
     Um único token por cor não serve: #16a34a como fundo com texto branco dá
     3,1:1 e reprova no AA, enquanto como texto sobre verde claro precisa ser
     ainda mais escuro. Todos os pares abaixo estão em >= 4,5:1.
-->
<style>
:root {
  --tb-surface: #ffffff;
  --tb-surface-alt: #f8f9fa;
  --tb-border: #dee2e6;
  --tb-text: #4b4b4b;
  --tb-text-muted: #767676;

  --tb-brand: #15488a;
  --tb-brand-soft: #eaf0f8;
  --tb-brand-text: #15488a;

  --tb-success: #15803d;
  --tb-success-soft: #e8f7ee;
  --tb-success-text: #15803d;
  --tb-success-line: #16a34a;

  --tb-warning-soft: #fdf6e3;
  --tb-warning-text: #7a5300;

  --tb-danger-soft: #fdeaea;
  --tb-danger-text: #b91c1c;

  --tb-idle: #6b7280;
  --tb-idle-soft: #f1f2f4;
  --tb-idle-text: #4b5563;
}

body.dark-layout {
  --tb-surface: #283046;
  --tb-surface-alt: #212840;
  --tb-border: #3b4253;
  --tb-text: #d0d2d6;
  --tb-text-muted: #a0a3ad;

  --tb-brand: #2f6bb3;
  --tb-brand-soft: #1f3555;
  --tb-brand-text: #8ab4e8;

  --tb-success: #15803d;
  --tb-success-soft: #14301f;
  --tb-success-text: #4ade80;
  --tb-success-line: #22c55e;

  --tb-warning-soft: #33290f;
  --tb-warning-text: #fbbf24;

  --tb-danger-soft: #3a1c1c;
  --tb-danger-text: #f87171;

  --tb-idle: #6b7280;
  --tb-idle-soft: #2c3247;
  --tb-idle-text: #a0a3ad;
}
</style>

<style scoped>
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

/* Override height for buttons with icons */
.btn-add,
.btn-back,
.btn-delete {
  height: auto !important;
  min-height: 30px;
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
}

/* Disabled state */
.btn-add:disabled,
.btn-back:disabled,
.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Toolbar */
.tb-toolbar {
  padding-bottom: 8px;
  border-bottom: 1px solid var(--tb-border);
}

/* Chips de filtro */
.tb-chips {
  gap: 8px;
}

.tb-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--tb-border);
  background: var(--tb-surface);
  color: var(--tb-text);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.tb-chip:hover {
  border-color: var(--tb-brand);
  color: var(--tb-brand-text);
}

.tb-chip[aria-pressed="true"] {
  background: var(--tb-brand);
  border-color: var(--tb-brand);
  color: #fff;
}

.tb-chip--boarded[aria-pressed="true"] {
  background: var(--tb-success);
  border-color: var(--tb-success);
  color: #fff;
}

.tb-chip-count {
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}

/* Card */
.tb-card {
  background: var(--tb-surface);
  border: 1px solid var(--tb-border);
  border-left: 4px solid var(--tb-idle);
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.15s;
}

.tb-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.tb-card--boarded {
  border-left-color: var(--tb-success-line);
}

.tb-card--available {
  border: 1px dashed var(--tb-border);
  border-left: 4px solid var(--tb-idle);
  background: transparent;
  box-shadow: none;
}

.tb-card--available:hover {
  border-color: var(--tb-brand);
  border-left-color: var(--tb-brand);
  background: var(--tb-surface);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.tb-card--available .tb-card-header {
  border-bottom: none;
  padding-bottom: 4px;
}

.tb-card--available .tb-card-footer {
  border-top: none;
  padding-top: 4px;
}

.tb-grid {
  transition: opacity 0.15s;
}

/* Dual-list do modal de embarque: lado a lado no desktop, empilhado abaixo
   de md — em telas estreitas duas colunas ficavam ilegíveis. */
.tb-dual {
  display: flex;
  gap: 12px;
  min-height: 340px;
}

.tb-table-scroll {
  overflow-x: auto;
}

@media (max-width: 767.98px) {
  .tb-dual {
    flex-direction: column;
    min-height: 0;
  }

  .tb-dual-arrows {
    flex-direction: row !important;
    min-width: 0;
    width: 100%;
  }

  .tb-chips {
    flex-wrap: wrap;
    margin-top: 8px;
  }

  .tb-toolbar {
    flex-wrap: wrap;
    row-gap: 8px;
  }

  .tb-clock {
    font-size: 1.2rem;
  }

  .tb-pager {
    justify-content: center !important;
  }
}

.tb-grid--loading {
  opacity: 0.45;
  pointer-events: none;
}

@media (min-width: 1600px) {
  .tb-card-col {
    flex: 0 0 25%;
    max-width: 25%;
  }
}

.tb-card-header {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  background: transparent;
  border-bottom: 1px solid var(--tb-border);
}

.tb-card-title {
  flex: 1 1 auto;
  min-width: 0;
}

.tb-team-name {
  font-size: 0.95rem;
  color: var(--tb-text);
}

.tb-status {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.tb-status--boarded {
  background: var(--tb-success-soft);
  color: var(--tb-success-text);
}

.tb-status--available {
  background: var(--tb-idle-soft);
  color: var(--tb-idle-text);
}

.tb-card-body {
  padding: 10px 12px;
}

/* Cronômetro de turno */
.tb-clock {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px 5px 9px;
  border-radius: 8px;
  font-family: "Courier New", Courier, monospace;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.15;
}

.tb-clock-value {
  font-variant-numeric: tabular-nums;
}

.tb-clock--ok {
  background: var(--tb-success-soft);
  color: var(--tb-success-text);
}

.tb-clock--warning {
  background: var(--tb-warning-soft);
  color: var(--tb-warning-text);
}

.tb-clock--danger {
  background: var(--tb-danger-soft);
  color: var(--tb-danger-text);
}

.tb-meta {
  margin-top: 5px;
  font-size: 0.74rem;
  color: var(--tb-text-muted);
}

/* Tripulação */
.tb-crew-label {
  margin: 12px 0 6px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--tb-text-muted);
}

.tb-crew {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tb-crew-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 6px;
  background: var(--tb-surface-alt);
  font-size: 0.8rem;
  min-width: 0;
}

.tb-avatar {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--tb-brand);
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tb-crew-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}

.tb-crew-name {
  color: var(--tb-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tb-crew-type {
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--tb-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tb-crew-empty {
  font-size: 0.78rem;
  color: var(--tb-text-muted);
}

/* Footer / ação */
.tb-card-footer {
  padding: 10px 12px;
  background: transparent;
  border-top: 1px solid var(--tb-border);
}

.tb-action {
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: center;
}

/* Paginação */
.tb-perpage {
  width: 72px;
}

.tb-pager {
  flex-wrap: wrap;
  gap: 8px;
}

/* Histórico */
.tb-history-card {
  background: var(--tb-surface);
  border: 1px solid var(--tb-border);
  border-radius: 6px;
}

.tb-history-header {
  padding: 12px;
  background: transparent;
  border-bottom: 1px solid var(--tb-border);
}

.tb-field-label {
  display: block;
  margin-bottom: 3px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--tb-text-muted);
}

.tb-quick-ranges {
  gap: 6px;
  flex-wrap: wrap;
}

.tb-chip--sm {
  padding: 3px 10px;
  font-size: 0.74rem;
}

.tb-inline-error {
  display: flex;
  align-items: center;
  padding: 7px 10px;
  border-radius: 6px;
  background: var(--tb-danger-soft);
  color: var(--tb-danger-text);
  font-size: 0.8rem;
}

/* Estados vazios do histórico */
.tb-history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  color: var(--tb-text-muted);
  max-width: 460px;
  margin: 0 auto;
}

.tb-history-empty h5 {
  color: var(--tb-text);
}

.tb-history-empty p {
  font-size: 0.82rem;
  line-height: 1.5;
}

/* Resumo do período */
/* Faixa de indicadores do período.
   Stat tile, não gráfico: são valores únicos de manchete. Os números usam
   figuras proporcionais de propósito — tabular-nums dá a todo dígito a
   largura do "0" e deixa o número frouxo em tamanho grande; tabular fica
   reservado às colunas da tabela, que precisam alinhar na vertical. */
.tb-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1px;
  background: var(--tb-border);
  border-bottom: 1px solid var(--tb-border);
}

.tb-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  background: var(--tb-surface-alt);
}

.tb-stat-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--tb-text-muted);
}

.tb-stat-value {
  display: inline-flex;
  align-items: center;
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.15;
  color: var(--tb-text);
}

/* "Em aberto" é estado, não série: cor sempre acompanhada de ícone. */
.tb-stat-value--active {
  color: var(--tb-success-text);
}

.tb-history-table thead th {
  padding: 8px 12px;
  background: transparent;
  border-top: none;
  border-bottom: 1px solid var(--tb-border);
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--tb-text-muted);
}

.tb-history-table tbody td {
  padding: 8px 12px;
  vertical-align: middle;
  border-top: 1px solid var(--tb-border);
  color: var(--tb-text);
  font-size: 0.82rem;
}

.tb-history-table tbody tr:hover td {
  background: var(--tb-surface-alt);
}

.tb-history-team {
  font-weight: 600;
}

.tb-num {
  font-variant-numeric: tabular-nums;
}

.tb-duration {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: "Courier New", Courier, monospace;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: 0.78rem;
}

.tb-duration--closed {
  background: var(--tb-idle-soft);
  color: var(--tb-idle-text);
}

.tb-duration--ok {
  background: var(--tb-success-soft);
  color: var(--tb-success-text);
}

.tb-duration--warning {
  background: var(--tb-warning-soft);
  color: var(--tb-warning-text);
}

.tb-duration--danger {
  background: var(--tb-danger-soft);
  color: var(--tb-danger-text);
}

/* Barra de filtros do histórico.
   Grid próprio em vez de col-md-*: as quatro larguras são desiguais (equipe
   é o campo mais largo, o botão só precisa do próprio texto) e o grid deixa
   o colapso responsivo explícito. */
.tb-history-title {
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid var(--tb-border);
}

.tb-history-filters {
  display: grid;
  grid-template-columns: minmax(140px, 1.6fr) minmax(120px, 1fr) minmax(120px, 1fr) auto;
  gap: 10px;
  align-items: end;
}

.tb-filter {
  min-width: 0;
}

.tb-filter--action {
  display: flex;
  align-items: flex-end;
}

.tb-window-hint {
  white-space: nowrap;
}

.tb-history-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--tb-border);
}

@media (max-width: 767.98px) {
  .tb-history-filters {
    grid-template-columns: 1fr 1fr;
  }

  .tb-filter--team {
    grid-column: 1 / -1;
  }

  .tb-filter--action {
    grid-column: 1 / -1;
  }

  .tb-window-hint {
    display: none;
  }

  .tb-history-pager {
    justify-content: center;
  }
}

/* Dual-list */
.dual-list-col {
  border: 1px solid var(--tb-border);
  background: var(--tb-surface);
}

.dual-list-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.85rem;
  border-bottom: 2px solid;
}

.dual-list-header--available {
  background: var(--tb-brand-soft);
  color: var(--tb-brand-text);
  border-color: var(--tb-brand);
}

.dual-list-header--selected {
  background: var(--tb-success-soft);
  color: var(--tb-success-text);
  border-color: var(--tb-success-line);
}

.dual-list-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dual-list-item {
  display: flex;
  align-items: center;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  user-select: none;
  font-size: 0.875rem;
  color: var(--tb-text);
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.dual-list-item--available {
  background: var(--tb-surface-alt);
  border-color: var(--tb-border);
}

.dual-list-item--available:hover {
  background: var(--tb-brand-soft);
  border-color: var(--tb-brand);
  color: var(--tb-brand-text);
}

.dual-list-item--selected {
  background: var(--tb-success-soft);
  border-color: var(--tb-success-line);
}

/* Clicar remove o profissional. O sinal fica na borda e no ícone, não
   repintando a linha inteira: fundo vermelho lia como erro e derrubava a
   legibilidade do nome no tema escuro. */
.dual-list-item--selected:hover {
  background: var(--tb-surface-alt);
  border-color: var(--tb-danger-text);
}

.dual-list-icon--remove {
  display: none;
  color: var(--tb-danger-text);
}

.dual-list-item--selected:hover .dual-list-icon--keep {
  display: none;
}

.dual-list-item--selected:hover .dual-list-icon--remove {
  display: inline-block;
}

.dual-list-icon--keep {
  color: var(--tb-success-text);
}

.dual-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--tb-text-muted);
  padding: 24px 0;
  text-align: center;
}

/* Setas centrais e contador */
.tb-dual-arrows {
  gap: 6px;
  min-width: 84px;
}

.tb-arrow-btn {
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.tb-selected-count {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--tb-brand-soft);
  color: var(--tb-brand-text);
  font-size: 0.78rem;
}
</style>
